import { Component } from 'react';
import isEqual from 'lodash-es/isEqual';
import cloneDeep from 'lodash-es/cloneDeep';
import classNames from 'classnames/bind';
import autoBind from 'react-autobind';
import { Dropdown } from 'react-bootstrap';

import { sentimentValues } from '../../../data/filters/storyFilters';
import { SILVER } from '../../../data/permissions';

import FilterButtonInfo from '../StoryFiltersButton/FilterButtonInfo';
import FilterInner from './FilterInner';
import FiltersMobileModal from '../FiltersMobileModal';

import Styles from '../story-filters-styles.module.scss';

const cx = classNames.bind(Styles);

class SentimentFilter extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasAccess: false,
      openFilterModal: false,
      dropdown: {
        open: false,
        title: 'All Sentiments',
        options: sentimentValues,
        sentiment_filter: 'categories',
        selectAll: true,
      },
    };
    autoBind(this);
  }

  componentDidMount() {
    this.generateData();
  }

  componentDidUpdate(prevProps) {
    const { savedFilterValue } = this.props;
    const { dropdown } = this.state;
    if (!isEqual(savedFilterValue, prevProps.savedFilterValue) && !dropdown.open) {
      this.generateData();
    }
  }

  handleCloseModal() {
    this.setState({ openFilterModal: false });
  }

  handleChangeFilterType(type) {
    const { hasAccess } = this.state;
    if (!hasAccess) return;

    const { actions, freezeStories } = this.props;
    freezeStories(false);
    actions.toggleSentimentFilterType(type);

    const { dropdown } = this.state;
    const {
      options,
      sentiment_down_limit: downLimit,
      sentiment_up_limit: upLimit,
    } = dropdown;

    const selectedCategories = options.filter((f) => f.select);
    let title = 'All Sentiments';
    let selectAll = true;
    if (selectedCategories.length === options.length) {
      title = 'All Sentiments';
      selectAll = true;
    } else {
      title = selectedCategories
        .map(({ name }) => name).join(', ') || 'Select Sentiment';
      selectAll = false;
    }
    if (type === 'range') {
      const downValue = downLimit || downLimit === 0 ? downLimit : -100;
      const upValue = upLimit || upLimit === 0 ? upLimit : 100;
      title = `${downValue}% <= ${upValue}%`;
    }

    this.setState({
      dropdown: {
        ...dropdown,
        title,
        sentiment_filter: type,
        selectAll,
      },
    });
  }

  handleCategoriesChange(value) {
    const { hasAccess } = this.state;
    const { piwikEnabled } = this.props;
    if (!hasAccess) return;

    const { actions, freezeStories } = this.props;
    freezeStories(false);

    const { dropdown: { options } } = this.state;
    const newOptions = cloneDeep(options);
    const foundIndex = newOptions.findIndex((f) => f.value === value);
    newOptions[foundIndex].select = !newOptions[foundIndex].select;
    const selectedCategories = newOptions.filter((f) => f.select);
    let title = 'All Sentiments';
    let selectAll = true;

    const valuesString = selectedCategories.map(({ value: objValue }) => objValue);
    actions.filterBySentimentCategories(valuesString);

    if (selectedCategories.length !== newOptions.length) {
      title = selectedCategories
        .filter((f) => f.select)
        .map(({ name }) => name).join(', ') || 'Select Sentiment';
      selectAll = false;
    }

    this.setState(({ dropdown }) => ({
      dropdown: {
        ...dropdown,
        title,
        options: newOptions,
        selectAll,
      },
    }));
    if (piwikEnabled) {
      _paq.push(['trackEvent', 'Sentiments', `Options: ${newOptions}`]);
    }
  }

  handleRangeChange(values) {
    const { hasAccess } = this.state;
    const { piwikEnabled } = this.props;
    if (!hasAccess) return;

    const { actions, freezeStories } = this.props;
    freezeStories(false);

    const downValue = values[0] || values[0] === 0 ? values[0] : -100;
    const upValue = values[1] || values[1] === 0 ? values[1] : 100;
    const title = `${downValue}% <= ${upValue}%`;
    actions.filterBySentimentRange({
      sentiment_down_limit: values[0],
      sentiment_up_limit: values[1],
    });
    this.setState(({ dropdown }) => ({
      dropdown: {
        ...dropdown,
        title,
        sentiment_down_limit: values[0],
        sentiment_up_limit: values[1],
      },
    }));
    if (piwikEnabled) {
      _paq.push(['trackEvent', 'Sentiments', `Down limit: ${values[0]} Up limit: ${values[1]}`]);
    }
  }

  selectAll() {
    const { hasAccess, dropdown } = this.state;
    const { piwikEnabled } = this.props;
    if (!hasAccess) return;

    const { actions, freezeStories } = this.props;
    freezeStories(false);
    const status = !dropdown.selectAll;
    const newOptions = cloneDeep(dropdown.options);

    let title = 'All Sentiments';
    let selectAll = true;
    const options = newOptions.map((option) => ({ ...option, select: status }));
    const selectedCategories = options.filter((f) => f.select);
    const valuesString = selectedCategories.map(({ value: objValue }) => objValue);
    actions.filterBySentimentCategories(valuesString);
    if (selectedCategories.length !== options.length) {
      title = selectedCategories
        .filter((f) => f.select)
        .map(({ name }) => name).join(', ') || 'Select Sentiment';
      selectAll = false;
    }

    this.setState({
      dropdown: {
        ...dropdown,
        title,
        selectAll,
        options,
      },
    });
    if (piwikEnabled) {
      _paq.push(['trackEvent', 'Sentiments', 'Select all']);
    }
  }

  generateData() {
    const { accessLevels, savedFilterValue } = this.props;
    const {
      sentiment_categories: categories,
      sentiment_down_limit: downLimit,
      sentiment_up_limit: upLimit,
      ...restObj
    } = savedFilterValue;

    const hasAccess = accessLevels.includes(SILVER);
    if (!hasAccess) {
      this.setState({
        hasAccess,
        dropdown: {
          title: 'All Sentiments',
          open: false,
          options: sentimentValues
            .map((obj) => ({ ...obj, select: true })),
          sentiment_filter: 'categories',
          sentiment_down_limit: -100,
          sentiment_up_limit: 100,
          selectAll: true,
        },
      });
      return;
    }

    let selectedCategories = sentimentValues;
    selectedCategories = selectedCategories
      .map((obj) => ({ ...obj, select: (categories || []).includes(obj.value) }));

    let title = 'All Sentiments';
    let selectAll = true;
    if (restObj.sentiment_filter === 'range') {
      const downValue = downLimit || downLimit === 0 ? downLimit : -100;
      const upValue = upLimit || upLimit === 0 ? upLimit : 100;
      title = `${downValue}% <= ${upValue}%`;
    } else if (categories.length === 5) {
      title = 'All Sentiments';
      selectAll = true;
    } else {
      title = selectedCategories
        .filter((f) => f.select)
        .map(({ name }) => name).join(', ') || 'Select Sentiment';
      selectAll = false;
    }

    this.setState({
      hasAccess,
      dropdown: {
        ...restObj,
        title,
        open: false,
        options: selectedCategories,
        sentiment_down_limit: downLimit,
        sentiment_up_limit: upLimit,
        selectAll,
      },
    });
  }

  toggleDropdown(status, event, source) {
    const { freezeStories, width, isSimpleLayout } = this.props;
    const { dropdown } = this.state;

    // workaround for IE11 to prevent double toggle when user clicks on
    // dropdown button to close dropdown
    if (
      (status === false
        && source.source === 'select')
      || (isSimpleLayout && width > 1024)
    ) {
      return;
    }

    if (width <= 1024) return this.openFilterModal();

    this.setState({
      dropdown: {
        ...dropdown,
        open: status,
      },
    }, () => {
      freezeStories(status);
    });
  }

  openFilterModal() {
    const { openFilterModal } = this.state;

    this.setState({ openFilterModal: !openFilterModal });
  }

  reset() {
    const { accessLevels } = this.props;
    const hasAccess = accessLevels.includes(SILVER);
    if (hasAccess) {
      this.setState({
        hasAccess,
        dropdown: {
          title: 'All Sentiments',
          open: false,
          options: sentimentValues
            .map((obj) => ({ ...obj, select: true })),
          sentiment_filter: 'categories',
          sentiment_down_limit: -100,
          sentiment_up_limit: 100,
          selectAll: true,
        },
      });
    }
  }

  render() {
    const {
      width, rbi, hasBigScreenDesign, isSimpleLayout, isSentimentLocation,
    } = this.props;
    const { dropdown, openFilterModal, hasAccess } = this.state;

    return (
      <Dropdown
        id={cx('sentiment-filter-dropdown')}
        className={cx('story-filters_btn', { disabled: !hasAccess || isSimpleLayout || isSentimentLocation, big_screen: hasBigScreenDesign }, { 'disabled-pointer-events': isSimpleLayout })}
        show={dropdown.open}
        onToggle={this.toggleDropdown}
      >
        <Dropdown.Toggle>
          <FilterButtonInfo
            dropdownTitle={(
              <span className={Styles.sentiment_title}>
                Sentiment
              </span>
            )}
            openFilter={dropdown.open}
            rbi={rbi}
            width={width}
            selectedValue={dropdown.title}
            buttonErr={!dropdown.selectAll && dropdown.title === 'Select Sentiment'}
          />
        </Dropdown.Toggle>
        {width >= 1025 ? (
          <Dropdown.Menu flip={false} bsPrefix="dropdown-menu">
            <FilterInner
              hasAccess={hasAccess}
              options={dropdown.options}
              sentimentFilter={dropdown.sentiment_filter}
              minRange={dropdown.sentiment_down_limit}
              maxRange={dropdown.sentiment_up_limit}
              handleChangeFilterType={this.handleChangeFilterType}
              handleCategoriesChange={this.handleCategoriesChange}
              handleRangeChange={this.handleRangeChange}
              hasBigScreenDesign={hasBigScreenDesign}
              selectAllStatus={dropdown.selectAll}
              selectAll={this.selectAll}
            />
          </Dropdown.Menu>
        ) : (
          <FiltersMobileModal
            openFilter={openFilterModal}
            handleCloseModal={this.handleCloseModal}
            classname={isSimpleLayout || isSentimentLocation ? 'disabledMessage' : ''}
            showCloseIcon
          >
            {isSimpleLayout || isSentimentLocation
              ? (
                <div className={cx('modalContainer')}>
                  <div className={cx('modalContent')}>
                    This filter is disabled for your
                    {' '}
                    current layout settings
                  </div>
                  <button
                    type="button"
                    className={cx('modalBtn')}
                    onClick={this.handleCloseModal}
                  >
                    OK
                  </button>
                </div>
              )
              : (
                <FilterInner
                  hasAccess={hasAccess}
                  options={dropdown.options}
                  sentimentFilter={dropdown.sentiment_filter}
                  minRange={dropdown.sentiment_down_limit}
                  maxRange={dropdown.sentiment_up_limit}
                  handleChangeFilterType={this.handleChangeFilterType}
                  handleCategoriesChange={this.handleCategoriesChange}
                  handleRangeChange={this.handleRangeChange}
                  selectAllStatus={dropdown.selectAll}
                  selectAll={this.selectAll}
                />
              )}
          </FiltersMobileModal>
        )}
        {(isSimpleLayout || isSentimentLocation) && (
          <div className={cx('tooltip')}>
            This filter is disabled for your
            <br />
            current layout settings
          </div>
        )}
      </Dropdown>
    );
  }
}

export default SentimentFilter;
